import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from './api';

// ==================== EMPLOYEES ====================
export interface EmployeeListItem {
  id: string;
  employeeNumber: string;
  firstName: string;
  lastName: string;
  corporateEmail: string;
  photoUrl?: string;
  status: string;
  hireDate: string;
  position: { id: string; title: string };
  department: { id: string; name: string };
  country: { id: string; code: string; nameEs: string };
  contractType: { id: string; name: string };
}

export function useEmployees(params: { search?: string; countryId?: string; departmentId?: string; status?: string; page?: number; pageSize?: number } = {}) {
  return useQuery({
    queryKey: ['employees', params],
    queryFn: async () => {
      const { data } = await api.get('/employees', { params });
      return data as { items: EmployeeListItem[]; total: number; page: number; pageSize: number };
    },
  });
}

export function useEmployee(id?: string) {
  return useQuery({
    queryKey: ['employee', id],
    enabled: !!id,
    queryFn: async () => (await api.get(`/employees/${id}`)).data,
  });
}

export function useOrgChart(countryId?: string) {
  return useQuery({
    queryKey: ['org-chart', countryId],
    queryFn: async () => (await api.get('/employees/org-chart', { params: { countryId } })).data,
  });
}

export function useCreateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => (await api.post('/employees', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
}

export function useUpdateEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: any }) =>
      (await api.put(`/employees/${id}`, payload)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['employees'] });
      qc.invalidateQueries({ queryKey: ['employee', vars.id] });
    },
  });
}

export function useDeleteEmployee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/employees/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['employees'] }),
  });
}

// ==================== LEAVE REQUESTS ====================
export interface LeaveRequest {
  id: string;
  employeeId: string;
  type: string;
  startDate: string;
  endDate: string;
  workingDays: number;
  reason?: string;
  status: string;
  exceedsSuggestedLimit: boolean;
  createdAt: string;
  employee: { id: string; firstName: string; lastName: string; photoUrl?: string };
}

export function useLeaveRequests(params: { status?: string; type?: string; employeeId?: string } = {}) {
  return useQuery({
    queryKey: ['leave-requests', params],
    queryFn: async () => (await api.get('/leave-requests', { params })).data as { items: LeaveRequest[]; total: number },
  });
}

export function usePendingLeaves() {
  return useQuery({
    queryKey: ['leave-requests', 'pending'],
    queryFn: async () => (await api.get('/leave-requests/pending')).data as { items: LeaveRequest[] },
  });
}

export function useMyBalance() {
  return useQuery({
    queryKey: ['leave-requests', 'my-balance'],
    queryFn: async () => (await api.get('/leave-requests/my-balance')).data,
  });
}

export function useCreateLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => (await api.post('/leave-requests', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leave-requests'] }),
  });
}

export function useApproveLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.put(`/leave-requests/${id}/approve`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leave-requests'] }),
  });
}

export function useRejectLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason: string }) =>
      (await api.put(`/leave-requests/${id}/reject`, { reason })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leave-requests'] }),
  });
}

export function useCancelLeave() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.put(`/leave-requests/${id}/cancel`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['leave-requests'] }),
  });
}

// ==================== CALENDAR ====================
export function useCalendar(from: string, to: string, countryId?: string) {
  return useQuery({
    queryKey: ['calendar', from, to, countryId],
    queryFn: async () => (await api.get('/calendar', { params: { from, to, countryId } })).data,
  });
}

// ==================== CATALOGS ====================
export function useCountries() {
  return useQuery({
    queryKey: ['catalogs', 'countries'],
    staleTime: 5 * 60_000,
    queryFn: async () => (await api.get('/catalogs/countries')).data as { items: any[] },
  });
}

export function useDepartments() {
  return useQuery({
    queryKey: ['catalogs', 'departments'],
    staleTime: 5 * 60_000,
    queryFn: async () => (await api.get('/catalogs/departments')).data as { items: any[] },
  });
}

export function usePositions() {
  return useQuery({
    queryKey: ['catalogs', 'positions'],
    staleTime: 5 * 60_000,
    queryFn: async () => (await api.get('/catalogs/positions')).data as { items: any[] },
  });
}

export function useContractTypes(countryId?: string) {
  return useQuery({
    queryKey: ['catalogs', 'contract-types', countryId],
    staleTime: 5 * 60_000,
    queryFn: async () => (await api.get('/catalogs/contract-types', { params: { countryId } })).data as { items: any[] },
  });
}

export function useHolidays(countryId?: string, year?: number) {
  return useQuery({
    queryKey: ['catalogs', 'holidays', countryId, year],
    queryFn: async () => (await api.get('/catalogs/holidays', { params: { countryId, year } })).data as { items: any[] },
  });
}

export function useCreateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => (await api.post('/catalogs/holidays', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalogs', 'holidays'] }),
  });
}

export function useUpdateHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: any }) =>
      (await api.put(`/catalogs/holidays/${id}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalogs', 'holidays'] }),
  });
}

export function useDeleteHoliday() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/catalogs/holidays/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalogs', 'holidays'] }),
  });
}

// ==================== CATALOG CRUD ====================
export function useCreateDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => (await api.post('/catalogs/departments', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalogs', 'departments'] }),
  });
}

export function useDeleteDepartment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/catalogs/departments/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalogs', 'departments'] }),
  });
}

export function useCreatePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => (await api.post('/catalogs/positions', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalogs', 'positions'] }),
  });
}

export function useDeletePosition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/catalogs/positions/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['catalogs', 'positions'] }),
  });
}

// ==================== USERS ====================
export function useUsers() {
  return useQuery({
    queryKey: ['users'],
    queryFn: async () => (await api.get('/users')).data as { items: any[] },
  });
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => (await api.post('/users', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

// ==================== NOTIFICATIONS ====================
export function useNotifications() {
  return useQuery({
    queryKey: ['notifications'],
    refetchInterval: 60_000,
    queryFn: async () => (await api.get('/notifications')).data as { items: any[]; unreadCount: number },
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.put(`/notifications/${id}/read`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.put('/notifications/mark-all-read')).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}

// ==================== AUDIT ====================
export function useAuditLog(params: { module?: string; action?: string; page?: number; pageSize?: number } = {}) {
  return useQuery({
    queryKey: ['audit-log', params],
    queryFn: async () => (await api.get('/audit-log', { params })).data,
  });
}

// ==================== DASHBOARD ====================
export function useDashboardStats() {
  return useQuery({
    queryKey: ['dashboard', 'stats'],
    queryFn: async () => (await api.get('/dashboard/stats')).data,
  });
}

// ==================== DOCUMENTS ====================
export function useEmployeeDocuments(employeeId?: string) {
  return useQuery({
    queryKey: ['documents', employeeId],
    enabled: !!employeeId,
    queryFn: async () => (await api.get(`/employees/${employeeId}/documents`)).data as { items: any[] },
  });
}

export function useInitDocumentUpload() {
  return useMutation({
    mutationFn: async ({ employeeId, payload }: { employeeId: string; payload: any }) =>
      (await api.post(`/employees/${employeeId}/documents/init-upload`, payload)).data as { document: any; uploadUrl: string },
  });
}

export function useUpdateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: any }) =>
      (await api.put(`/documents/${id}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

export function useDeleteDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/documents/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

export function useDocumentDownloadUrl() {
  return useMutation({
    mutationFn: async ({ id, inline }: { id: string; inline?: boolean }) =>
      (await api.get(`/documents/${id}/download`, { params: inline ? { inline: 1 } : {} })).data as { url: string },
  });
}

export function useConfirmDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status?: string }) =>
      (await api.put(`/documents/${id}/confirm`, { status: status ?? 'VALID' })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['documents'] }),
  });
}

// ==================== PHOTO ====================
export function useInitPhotoUpload() {
  return useMutation({
    mutationFn: async ({ employeeId, payload }: { employeeId: string; payload: any }) =>
      (await api.post(`/employees/${employeeId}/photo/init-upload`, payload)).data as { uploadUrl: string; s3Key: string },
  });
}

export function useConfirmPhoto() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ employeeId, s3Key }: { employeeId: string; s3Key: string }) =>
      (await api.put(`/employees/${employeeId}/photo`, { s3Key })).data as { photoUrl: string },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['employee', vars.employeeId] });
      qc.invalidateQueries({ queryKey: ['employees'] });
    },
  });
}

// ==================== EVALUATIONS ====================
export function useEvaluations(employeeId?: string) {
  return useQuery({
    queryKey: ['evaluations', employeeId],
    enabled: !!employeeId,
    queryFn: async () => (await api.get(`/employees/${employeeId}/evaluations`)).data as { items: any[] },
  });
}

export function useCreateEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ employeeId, payload }: { employeeId: string; payload: any }) =>
      (await api.post(`/employees/${employeeId}/evaluations`, payload)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['evaluations', vars.employeeId] });
      qc.invalidateQueries({ queryKey: ['employee', vars.employeeId] });
    },
  });
}

export function useUpdateEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: any }) =>
      (await api.put(`/evaluations/${id}`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['evaluations'] }),
  });
}

export function useDeleteEvaluation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/evaluations/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['evaluations'] }),
  });
}

// ==================== TRAININGS ====================
export function useTrainings(employeeId?: string) {
  return useQuery({
    queryKey: ['trainings', employeeId],
    enabled: !!employeeId,
    queryFn: async () => (await api.get(`/employees/${employeeId}/trainings`)).data as { items: any[] },
  });
}

export function useCreateTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ employeeId, payload }: { employeeId: string; payload: any }) =>
      (await api.post(`/employees/${employeeId}/trainings`, payload)).data,
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['trainings', vars.employeeId] }),
  });
}

export function useDeleteTraining() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/trainings/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['trainings'] }),
  });
}

// ==================== COMPLIANCE ====================
export function useUpdateCompliance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ employeeId, payload }: { employeeId: string; payload: any }) =>
      (await api.put(`/employees/${employeeId}/compliance`, payload)).data,
    onSuccess: (_, vars) => qc.invalidateQueries({ queryKey: ['employee', vars.employeeId] }),
  });
}

// ==================== POLICIES ====================
export function usePolicies() {
  return useQuery({
    queryKey: ['policies'],
    queryFn: async () => (await api.get('/policies')).data as { items: any[] },
  });
}

export function useUpsertPolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => (await api.post('/policies', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });
}

export function useDeletePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) => (await api.delete(`/policies/${key}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['policies'] }),
  });
}

export function useUpdateUserRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, role }: { id: string; role: string }) =>
      (await api.put(`/users/${id}/role`, { role })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['users'] }),
  });
}

// ==================== ANNOUNCEMENTS ====================
export interface AnnouncementListItem {
  id: string;
  title: string;
  body: string;
  status: string;
  pinned: boolean;
  audienceType: string;
  audienceCountryIds: string[];
  audienceDepartmentIds: string[];
  attachmentName?: string;
  attachmentSize?: number;
  createdBy: {
    id: string;
    email: string;
    employee?: { firstName: string; lastName: string; photoUrl?: string; position?: { title: string } };
  };
  isRead: boolean;
  readCount: number;
  totalAudience: number;
  publishedAt?: string;
  createdAt: string;
}

export function useAnnouncements(params: { status?: string; search?: string; page?: number; pageSize?: number } = {}) {
  return useQuery({
    queryKey: ['announcements', params],
    queryFn: async () => (await api.get('/announcements', { params })).data as { items: AnnouncementListItem[]; total: number; page: number; pageSize: number },
  });
}

export function useAnnouncement(id?: string) {
  return useQuery({
    queryKey: ['announcement', id],
    enabled: !!id,
    queryFn: async () => (await api.get(`/announcements/${id}`)).data,
  });
}

export function useAnnouncementUnreadCount() {
  return useQuery({
    queryKey: ['announcements', 'unread-count'],
    refetchInterval: 60_000,
    queryFn: async () => (await api.get('/announcements/unread-count')).data as { count: number },
  });
}

export function useCreateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: any) => (await api.post('/announcements', payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['announcements'] });
    },
  });
}

export function useUpdateAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: any }) =>
      (await api.put(`/announcements/${id}`, payload)).data,
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['announcements'] });
      qc.invalidateQueries({ queryKey: ['announcement', vars.id] });
    },
  });
}

export function useDeleteAnnouncement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`/announcements/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });
}

export function useInitAnnouncementAttachment() {
  return useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: { fileName: string; mimeType: string; size: number } }) =>
      (await api.post(`/announcements/${id}/attachment`, payload)).data as { uploadUrl: string; s3Key: string },
  });
}

export function useAnnouncementAttachmentUrl() {
  return useMutation({
    mutationFn: async (id: string) =>
      (await api.get(`/announcements/${id}/attachment`)).data as { url: string },
  });
}

// ==================== AUTH EXTRAS ====================
export function useChangePassword() {
  return useMutation({
    mutationFn: async (payload: { currentPassword: string; newPassword: string }) =>
      (await api.post('/auth/change-password', payload)).data,
  });
}
